/**
 * Carousel Initialization Script
 * Enhances elements with `carousel` or `is-style-carousel` classes by converting them into Swiper.js instances.
 * Supports customization such as navigation, pagination, autoplay, vertical direction, and more.
 */

document.addEventListener("DOMContentLoaded", () => {
	// Select all carousel elements
	const carousels = document.querySelectorAll(
		".carousel, .is-style-carousel",
	);

	// Iterate over each carousel element
	carousels.forEach((carousel) => {
		// Add the 'swiper' class required by Swiper.js
		carousel.classList.add("swiper");

		// Create a Swiper wrapper and move all child elements into it
		const wrapper = document.createElement("div");
		wrapper.classList.add("swiper-wrapper");

		Array.from(carousel.children).forEach((child) => {
			child.classList.add("swiper-slide"); // Add 'swiper-slide' class to each child
			wrapper.appendChild(child); // Append child to Swiper wrapper
		});

		// Clear the original carousel content and append the new Swiper wrapper
		carousel.innerHTML = "";
		carousel.appendChild(wrapper);

		// Add navigation buttons if the carousel requires arrows
		if (carousel.classList.contains("has-arrows")) {
			const nextBtn = document.createElement("div");
			nextBtn.className = "swiper-button-next";

			const prevBtn = document.createElement("div");
			prevBtn.className = "swiper-button-prev";

			carousel.appendChild(nextBtn);
			carousel.appendChild(prevBtn);
		}

		// Add pagination if the carousel requires dots
		if (carousel.classList.contains("has-dots")) {
			const pagination = document.createElement("div");
			pagination.className = "swiper-pagination";

			carousel.appendChild(pagination);
		}

		/**
		 * Helper function to parse gap values (e.g., from CSS clamp function or other units).
		 * Extracts gap size in pixels.
		 * @param {string} val - The CSS value for the gap
		 * @returns {number} - The parsed gap value in pixels
		 */
		const parseGapValue = (val) => {
			val = (val || "").trim();
			if (!val) return 0;

			const parts = val.split(/\s+/);
			const lastPart = parts[parts.length - 1];

			// Check for `clamp()` and extract the pixel value
			const clampMatch = lastPart.match(/clamp\(([^,]+),/);
			if (clampMatch) {
				const pxMatch = clampMatch[1].match(/(\d+(\.\d+)?)\s*px/);
				if (pxMatch) return parseFloat(pxMatch[1]);
			}

			// Check for a basic pixel value
			const pxMatch = lastPart.match(/(\d+(\.\d+)?)\s*px/);
			if (pxMatch) return parseFloat(pxMatch[1]);

			return 0; // Default to 0 if no match
		};

		// Get the gap size between slides from the computed CSS styles
		const spaceBetween = parseGapValue(getComputedStyle(carousel).gap);

		// Configure Swiper options based on carousel classes
		const swiperOptions = {
			loop: carousel.classList.contains("is-infinite"),
			direction: carousel.classList.contains("is-vertical")
				? "vertical"
				: "horizontal",
			autoplay: carousel.classList.contains("will-autoplay")
				? bds_theme.features.carousels.swiper_opts.autoplay // Assume `bds_theme` provides autoplay options
				: false,
			effect: carousel.classList.contains("will-fade") ? "fade" : "slide",
			navigation: carousel.classList.contains("has-arrows")
				? {
						nextEl: carousel.querySelector(".swiper-button-next"),
						prevEl: carousel.querySelector(".swiper-button-prev"),
					}
				: false,
			pagination: carousel.classList.contains("has-dots")
				? {
						el: carousel.querySelector(".swiper-pagination"),
						clickable: true,
					}
				: false,
			speed: bds_theme.features.carousels.swiper_opts.speed, // Carousel transition speed
			spaceBetween, // Space between slides
		};

		// Enable fade effect options if the `will-fade` class is present
		if (carousel.classList.contains("will-fade")) {
			swiperOptions.fadeEffect =
				bds_theme.features.carousels.swiper_opts.fade_effect;
		}

		// Determine the number of slides to show (`show-X` classes)
		let slidesPerView = 1; // Default to 1 slide per view
		for (let i = 1; i <= 6; i++) {
			if (carousel.classList.contains(`show-${i}`)) {
				slidesPerView = i;
				break;
			}
		}
		swiperOptions.slidesPerView = slidesPerView;

		// Determine the number of slides to scroll at a time (`scroll-X` classes)
		let slidesPerGroup = 1; // Default to 1 slide per group
		for (let i = 1; i <= 6; i++) {
			if (carousel.classList.contains(`scroll-${i}`)) {
				slidesPerGroup = i;
				break;
			}
		}
		swiperOptions.slidesPerGroup = slidesPerGroup;

		// Initialize Swiper instance for the carousel
		new Swiper(carousel, swiperOptions);
	});
});
